import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getRegulations } from 'Services/overviewApi';
import classes from './RegulationSelector.module.scss';

const RegulationSelector = ({ clientCode, onRegulationChange }) => {
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRegulation, setSelectedRegulation] = useState(null);
  const dropdownRef = useRef(null);

  // Load regulations on mount or when clientCode changes
  useEffect(() => {
    const loadRegulations = async () => {
      if (!clientCode) {
        setLoading(false);
        return;
      }

      try {
        const response = await getRegulations();
        const regulationList = response?.value || [];
        // Filter to only visible regulations
        const visibleRegulations = regulationList.filter((r) => r.IsVisible !== false);
        setRegulations(visibleRegulations);

        // Check session storage for previously selected regulation
        const storedRegulationId = sessionStorage.getItem('selectedRegulationId');
        const storedRegulationName = sessionStorage.getItem('selectedRegulationName');

        let initialRegulation = null;
        if (storedRegulationId && storedRegulationName) {
          initialRegulation = visibleRegulations.find(
            (r) => r.Id === Number.parseInt(storedRegulationId, 10) || r.Name === storedRegulationName,
          );
        }

        // Default to PRIIPS if no stored selection or not found
        if (!initialRegulation) {
          initialRegulation = visibleRegulations.find((r) => r.Name === 'PRIIPS') || visibleRegulations[0];
        }

        if (initialRegulation) {
          setSelectedRegulation(initialRegulation);
          saveToSessionStorage(initialRegulation);
          if (onRegulationChange) {
            onRegulationChange(initialRegulation);
          }
        }
      } catch (err) {
        console.error('Failed to load regulations:', err);
        setRegulations([]);
      } finally {
        setLoading(false);
      }
    };

    loadRegulations();
  }, [clientCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveToSessionStorage = (regulation) => {
    if (regulation) {
      sessionStorage.setItem('selectedRegulationId', regulation.Id);
      sessionStorage.setItem('selectedRegulationName', regulation.Name);
      sessionStorage.setItem('selectedRegulationReportTypes', JSON.stringify(regulation.ReportTypes || []));

      // Update AppStateStorage with selectedRegulation
      // Note: AppStateStorage is encrypted, so we store regulation separately
      // and let components read from sessionStorage directly
    }
  };

  const handleSelect = useCallback((regulation) => {
    setSelectedRegulation(regulation);
    setIsOpen(false);

    // Clear fund/class selections when regulation changes
    sessionStorage.removeItem('selectedFundCode');
    sessionStorage.removeItem('selectedFundGroupCode');
    sessionStorage.removeItem('searchBoxValue');

    saveToSessionStorage(regulation);

    if (onRegulationChange) {
      onRegulationChange(regulation);
    }
  }, [onRegulationChange]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const getReportTypeCategories = (regulation) => {
    if (!regulation?.ReportTypes) return '';

    const categories = regulation.ReportTypes
      .filter((t) => {
        // For PRIIPS, exclude category 6; for others, exclude category 3
        const categoryId = t.ReportTypeCategory?.ReportTypeCategoryId;
        return regulation.Name === 'PRIIPS'
          ? categoryId !== 6
          : categoryId !== 3;
      })
      .map((t) => t.ReportTypeCategory?.CategoryName)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index); // Distinct

    return categories.join(', ');
  };

  if (loading) {
    return (
      <div className={classes.regulationSelector}>
        <button type="button" className={classes.regulationButton} disabled>
          <span className={classes.buttonContent}>
            <span className={classes.buttonRegulation}>Loading...</span>
          </span>
        </button>
      </div>
    );
  }

  if (regulations.length === 0) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      className={`${classes.regulationSelector} ${isOpen ? classes.activeDropdown : ''}`}
    >
      <button
        type="button"
        className={classes.regulationButton}
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className={classes.buttonContent}>
          <span className={classes.buttonRegulation}>
            {selectedRegulation?.Name || 'Select Regulation'}
          </span>
          <span className={classes.buttonReports}>
            {selectedRegulation ? getReportTypeCategories(selectedRegulation) : ''}
          </span>
        </span>
        <span className={classes.dropdownArrow}>&#9662;</span>
      </button>

      {isOpen && (
        <div className={classes.dropdownMenu} role="menu">
          {regulations.map((regulation) => (
            <button
              key={regulation.Id}
              type="button"
              className={classes.dropdownItem}
              onClick={() => handleSelect(regulation)}
              role="menuitem"
            >
              <div className={classes.itemRegulation}>{regulation.Name}</div>
              <div className={classes.itemReports}>
                {getReportTypeCategories(regulation)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

RegulationSelector.propTypes = {
  clientCode: PropTypes.string,
  onRegulationChange: PropTypes.func,
};

RegulationSelector.defaultProps = {
  clientCode: null,
  onRegulationChange: null,
};

export default RegulationSelector;
